/**
 * Entity-aware text utilities.
 *
 * Telegram MTProto stores formatting as a list of (type, offset, length) entities
 * over UTF-16 code units. The editor mirrors that model: a plain string + an
 * array of entities. These helpers keep entities consistent when text is edited.
 */

import type { MessageEntity } from "@/api/templates";

/** Telegram counts offsets in UTF-16 code units, same as JavaScript string indexing. */
export function utf16Len(s: string): number {
  return s.length;
}

/**
 * Apply a text edit (replace [from, to) with `replacement`) and shift entities
 * accordingly. Entities fully inside the deleted span are removed; entities
 * overlapping the boundary are truncated to the surviving side.
 */
export function spliceText(
  text: string,
  entities: MessageEntity[],
  from: number,
  to: number,
  replacement: string,
): { text: string; entities: MessageEntity[] } {
  const delta = replacement.length - (to - from);
  const next = text.slice(0, from) + replacement + text.slice(to);
  const out: MessageEntity[] = [];
  for (const e of entities) {
    const start = e.offset;
    const end = e.offset + e.length;
    // Entity entirely before edit
    if (end <= from) {
      out.push(e);
      continue;
    }
    // Entity entirely after edit
    if (start >= to) {
      out.push({ ...e, offset: start + delta });
      continue;
    }
    // Overlaps the deletion span. Try to preserve the surviving portion.
    const newStart = Math.min(start, from);
    const newEnd = Math.max(end + delta, from + replacement.length);
    // Clip to the part that doesn't intersect the deleted span
    const clipStart = Math.max(newStart, from);
    const clipEnd = Math.min(newEnd, from + replacement.length);
    if (clipEnd > clipStart) {
      // Entity that straddled is preserved only if it covered the new replacement range too
      // — pragmatically: keep formatting on the head if any survived.
    }
    const headEnd = Math.min(end, from);
    if (headEnd > start) {
      out.push({ ...e, offset: start, length: headEnd - start });
    }
    const tailStart = Math.max(start, to);
    if (tailStart < end) {
      out.push({
        ...e,
        offset: tailStart + delta,
        length: end - tailStart,
      });
    }
  }
  return { text: next, entities: mergeAdjacent(out) };
}

/** Add an entity for a selection. Auto-merges adjacent entities of same type. */
export function applyEntity(
  entities: MessageEntity[],
  add: MessageEntity,
): MessageEntity[] {
  if (add.length <= 0) return entities;
  return mergeAdjacent([...entities, add]);
}

/** Toggle a simple formatting entity (bold/italic/...) on a range. */
export function toggleEntity(
  entities: MessageEntity[],
  type: MessageEntity["type"],
  offset: number,
  length: number,
): MessageEntity[] {
  if (length <= 0) return entities;
  const end = offset + length;
  const overlapping = entities.filter(
    (e) => e.type === type && e.offset < end && e.offset + e.length > offset,
  );
  if (overlapping.length > 0) {
    // Strip the selected range from every overlapping entity of this type
    const out: MessageEntity[] = entities.filter((e) => !overlapping.includes(e));
    for (const e of overlapping) {
      if (e.offset < offset) {
        out.push({ ...e, offset: e.offset, length: offset - e.offset });
      }
      if (e.offset + e.length > end) {
        out.push({ ...e, offset: end, length: e.offset + e.length - end });
      }
    }
    return mergeAdjacent(out);
  }
  return mergeAdjacent([...entities, { type, offset, length }]);
}

function mergeAdjacent(entities: MessageEntity[]): MessageEntity[] {
  const sorted = [...entities]
    .filter((e) => e.length > 0)
    .sort((a, b) => a.offset - b.offset || a.length - b.length);
  const out: MessageEntity[] = [];
  for (const e of sorted) {
    const prev = out[out.length - 1];
    const sameKind =
      prev &&
      prev.type === e.type &&
      prev.url === e.url &&
      prev.user_id === e.user_id &&
      prev.language === e.language &&
      prev.custom_emoji_id === e.custom_emoji_id &&
      prev.custom_emoji_is_free === e.custom_emoji_is_free;
    if (sameKind && prev.offset + prev.length >= e.offset) {
      prev.length = Math.max(prev.length, e.offset + e.length - prev.offset);
    } else {
      out.push({ ...e });
    }
  }
  return out;
}

/**
 * Render text + entities to highlighted HTML segments for the preview.
 * Returns a flat list of {text, types[]} so React can wrap with styled spans.
 */
export interface RenderSpan {
  text: string;
  types: MessageEntity[];
}

export function renderSpans(text: string, entities: MessageEntity[]): RenderSpan[] {
  if (entities.length === 0) return [{ text, types: [] }];
  // Build a list of boundaries
  const boundaries = new Set<number>([0, text.length]);
  for (const e of entities) {
    boundaries.add(e.offset);
    boundaries.add(e.offset + e.length);
  }
  const sortedBoundaries = [...boundaries]
    .filter((b) => b >= 0 && b <= text.length)
    .sort((a, b) => a - b);

  const spans: RenderSpan[] = [];
  for (let i = 0; i < sortedBoundaries.length - 1; i++) {
    const start = sortedBoundaries[i];
    const end = sortedBoundaries[i + 1];
    if (end <= start) continue;
    const types = entities.filter((e) => e.offset <= start && e.offset + e.length >= end);
    spans.push({ text: text.slice(start, end), types });
  }
  return spans;
}
